
import * as api from '../api';
import * as proj from './projections';

/**
 * Parse coordinates in various formats
 * @param {string} input 
 * @param {L.Map} map
 * @returns {Promise<L.LatLng|null>}
 */
export async function parseCoordinates(input, map) {
    if (!input) return null;

    // Normalize input: remove extra spaces, replace common separators
    let normalized = input.trim().replace(/\s+/g, ' ');

    // Normalize decimal separator: replace comma between digits with a dot
    normalized = normalized.replace(/,/g, (match, offset, string) => {
        // Only replace comma if it looks like a decimal separator (between digits)
        if (offset > 0 && offset < string.length - 1 && /\d/.test(string[offset-1]) && /\d/.test(string[offset+1])) {
            return '.';
        }
        return match;
    });

    // --- Format UTM : "32T 212233 5651155" ou "32 212233 5651155" ---
    const utmRegex = /^(\d{1,2})([A-Za-z])?[-\s](\d+)[-\s](\d+)$/;
    const utmMatch = normalized.match(utmRegex);
    if (utmMatch) {
        const zone = parseInt(utmMatch[1]);
        const band = utmMatch[2];
        const easting = parseFloat(utmMatch[3]);
        const northing = parseFloat(utmMatch[4]);
        
        // Utilisation de proj4 si disponible, sinon fallback sur projections.js
        if (typeof proj4 !== 'undefined') {
            let utmDef = "+proj=utm +zone=" + zone;
            
            // Si la bande est présente, on peut déterminer si on est dans l'hémisphère sud
            // Les bandes N à X sont dans le nord, C à M sont dans le sud
            if (band) {
                const bandUpper = band.toUpperCase();
                if (bandUpper >= 'C' && bandUpper <= 'M') {
                    utmDef += " +south";
                }
            } else if (zone === 40) {
                // Fallback spécifique pour la zone 40 (souvent utilisée dans l'hémisphère sud dans ce projet)
                utmDef += " +south";
            }
            
            utmDef += " +ellps=WGS84 +datum=WGS84 +units=m +no_defs";
            
            try {
                const coord = proj4(utmDef, 'EPSG:4326', [easting, northing]);
                if (isValid(coord[1], coord[0])) return L.latLng(coord[1], coord[0]);
            } catch (e) {
                console.error("UTM conversion with proj4 failed", e);
            }
        }
    }

    // --- Format DMS: "N 45° 01' 23.4\" E 5° 01' 23.4\"" ou "45 01 23.4 5 01 23.4" ---
    // Logique utilisateur: reg = /[,.°' ]/; tableau = value.split(reg);
    const dmsRegex = /^[NSEWnsew]?\s*(\d+)[°\s,]+(\d+)['\s,]+(\d+\.?\d*)["\s,]*[NSEWnsew]?\s*(\d+)[°\s,]+(\d+)['\s,]+(\d+\.?\d*)["\s,]*[NSEWnsew]?$/;
    const dmsMatch = normalized.match(dmsRegex);
    if (dmsMatch) {
        // Simple extraction based on user's switch DMS logic
        const reg = /[,°' ]+/;
        const tableau = normalized.split(reg).filter(s => s !== "");
        
        // Match user's tableau indexes (approx)
        // case "DMS": if (tableau[0] == "N") { latitude = parseFloat(tableau[1]) + conv_lat_min + conv_lat_sec; }
        let lat, lng;
        let offset = 0;
        let latSign = 1;
        let lngSign = 1;

        if (/[Ssw-]/.test(tableau[0])) { latSign = -1; offset = 1; }
        else if (/[Nnw+]/.test(tableau[0])) { offset = 1; }

        lat = (parseFloat(tableau[offset]) + parseFloat(tableau[offset+1])/60 + parseFloat(tableau[offset+2])/3600) * latSign;
        
        let lngOffset = offset + 3;
        if (/[Ww-]/.test(tableau[lngOffset])) { lngSign = -1; lngOffset++; }
        else if (/[Ee+]/.test(tableau[lngOffset])) { lngOffset++; }

        lng = (parseFloat(tableau[lngOffset]) + parseFloat(tableau[lngOffset+1])/60 + parseFloat(tableau[lngOffset+2])/3600) * lngSign;

        if (isValid(lat, lng)) return L.latLng(lat, lng);
    }

    // --- Format DM: "N 45° 01.23 E 5° 01.23" ---
    const dmRegex = /^[NSEWnsew]?\s*(\d+)[°\s,]+(\d+\.?\d*)['\s,]*[NSEWnsew]?\s*(\d+)[°\s,]+(\d+\.?\d*)['\s,]*[NSEWnsew]?$/;
    const dmMatch = normalized.match(dmRegex);
    if (dmMatch) {
        const reg = /[,°' ]+/;
        const tableau = normalized.split(reg).filter(s => s !== "");
        let lat, lng;
        let offset = 0;
        let latSign = 1;
        let lngSign = 1;

        if (/[Ssw-]/.test(tableau[0])) { latSign = -1; offset = 1; }
        else if (/[Nnw+]/.test(tableau[0])) { offset = 1; }

        lat = (parseFloat(tableau[offset]) + parseFloat(tableau[offset+1])/60) * latSign;

        let lngOffset = offset + 2;
        if (/[Ww-]/.test(tableau[lngOffset])) { lngSign = -1; lngOffset++; }
        else if (/[Ee+]/.test(tableau[lngOffset])) { lngOffset++; }

        lng = (parseFloat(tableau[lngOffset]) + parseFloat(tableau[lngOffset+1])/60) * lngSign;

        if (isValid(lat, lng)) return L.latLng(lat, lng);
    }

    // 1. Decimal Degrees (DD): e.g., "45.123, 5.456" or "45.123 5.456"
    const ddMatch = normalized.match(/^(-?\d+\.?\d*)[, ]\s*(-?\d+\.?\d*)$/);
    if (ddMatch) {
        const lat = parseFloat(ddMatch[1]);
        const lng = parseFloat(ddMatch[2]);
        if (isValid(lat, lng)) return L.latLng(lat, lng);
    }

    // 2. Degrees Decimal Minutes (DDM): e.g., "45° 7.38' N, 5° 27.36' E" or "45 7.38 -5 27.36"
    // Regex for DDM (simplified)
    const ddmRegex = /^(\d+)[°\s]+(\d+\.?\d*)['\s]*([NSEWnsew-])?[, ]\s*(\d+)[°\s]+(\d+\.?\d*)['\s]*([NSEWnsew-])?$/;
    const ddmMatch = normalized.match(ddmRegex);
    if (ddmMatch) {
        let lat = parseInt(ddmMatch[1]) + parseFloat(ddmMatch[2]) / 60;
        if (ddmMatch[3] && /[SWsw-]/.test(ddmMatch[3])) lat = -lat;
        
        let lng = parseInt(ddmMatch[4]) + parseFloat(ddmMatch[5]) / 60;
        if (ddmMatch[6] && /[SWsw-]/.test(ddmMatch[6])) lng = -lng;

        if (isValid(lat, lng)) return L.latLng(lat, lng);
    }

    // 4. What3Words (W3W): e.g., "///varier.prouver.rejoindre"
    const w3wRegex = /^\/{3}([a-zA-Z\u00C0-\u017F]+)\.([a-zA-Z\u00C0-\u017F]+)\.([a-zA-Z\u00C0-\u017F]+)$/i;
    const w3wMatch = normalized.match(w3wRegex);
    if (w3wMatch) {
        try {
            const response = await api.callService('what3words', { words: w3wMatch[0].replace('///', '') });
            const results = response['what3words'];
            if (results && results.length > 0 && results[0].lat && results[0].lng) {
                return L.latLng(results[0].lat, results[0].lng);
            }
        } catch (e) {
            console.error("W3W search failed", e);
        }
    }

    // 5. Google Plus Code: e.g., "8FW4V75V+H5"
    const plusCodeRegex = /^([23456789CFGHJMPQRVQX]{8}\+[23456789CFGHJMPQRVQX]{2,3}|^[23456789CFGHJMPQRVQX]{4}\+[23456789CFGHJMPQRVQX]{2,3})$/i;
    const plusCodeMatch = normalized.match(plusCodeRegex);
    if (plusCodeMatch) {
        try {
            const mapCenter = map.getCenter();
            const response = await api.callService('pluscode', {
                code: plusCodeMatch[1],
                lat: mapCenter.lat,
                lng: mapCenter.lng
            });
            const results = response['pluscode'];
            if (results && results.length > 0 && results[0].lat && results[0].lng) {
                return L.latLng(results[0].lat, results[0].lng);
            }
        } catch (e) {
            console.error("Plus Code search failed", e);
        }
    }

    return null;
}

function isValid(lat, lng) {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

export function coordinateSearch(map) {
    window.searchCoordinates = async (event) => {
        if (event.type === 'keyup' && event.key !== 'Enter') return;

        const input = document.getElementById('coordinates-query').value;
        const errorDiv = document.getElementById('coordinates-error');
        const latlng = await parseCoordinates(input, map);
        // Récupérer le template Handlebars
        const template = Handlebars.templates['coordinates-result'];
        const isEventDetails = map.eventId !== null && map.eventId !== undefined;

        if (latlng) {
            // Données à injecter dans le template
            const popupHtmlcoord = template({
            lat: latlng.lat,
            lng: latlng.lng,
            isEventDetails: isEventDetails
            });
            errorDiv.style.display = 'none';
            L.marker(latlng)
                .addTo(map)
                .bindPopup(popupHtmlcoord)
                .openPopup();
            map.setView(latlng, 16);
        } else {
            errorDiv.style.display = 'block';
        }
    };
}
